import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { PageHeader, AdminCard, StatusPill, PageLoader, Btn, datetimeFromIso } from "@/admin/components";

export default function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [errors, setErrors] = useState(null);

  const load = useCallback(async () => {
    const [h, e] = await Promise.all([
      adminApi.get("/admin/system-health"),
      adminApi.get("/admin/errors"),
    ]);
    setHealth(h.data);
    setErrors(e.data.errors);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const clearErrors = async () => {
    const { data } = await adminApi.post("/admin/errors/clear");
    toast.success(`${data.cleared} resolved error(s) cleared`);
    load();
  };

  if (!health) return <PageLoader />;

  return (
    <div data-testid="admin-system-health-page">
      <PageHeader title="System Health" subtitle="Service status · refreshes every 30s" />

      <div className="grid grid-cols-3 gap-4">
        {health.services.map((s) => (
          <AdminCard key={s.name} className="p-5" data-testid={`health-${s.name.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <p className="text-[14px] font-semibold text-[#111]">{s.name}</p>
              <StatusPill status={s.status} label={s.status} />
            </div>
            <p className="mt-3 text-[12px] text-[#999]">Checked {datetimeFromIso(s.last_checked)}</p>
          </AdminCard>
        ))}
      </div>

      <AdminCard className="mt-6 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-[#111]">Recent Errors</h3>
          <Btn variant="light" onClick={clearErrors} testid="clear-errors-btn">Clear resolved errors</Btn>
        </div>
        {!errors || errors.length === 0 ? (
          <p className="py-6 text-center text-[14px] text-[#999]">No errors logged. All clear. ✅</p>
        ) : (
          <div className="divide-y divide-[#f2efea]">
            {errors.map((e) => (
              <div key={e.id} className="flex items-start gap-3 border-l-2 border-l-[#c0392b] py-3 pl-3">
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-[#111]">{e.error_type}</p>
                  <p className="text-[13px] text-[#666]">{e.error_message.slice(0, 120)}</p>
                </div>
                <span className="shrink-0 text-[12px] text-[#999]">{datetimeFromIso(e.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </AdminCard>
    </div>
  );
}
